import React from "react";
import LegalPageLayout from "@/components/LegalPageLayout";
import { SITE } from "@/lib/site";

const sections = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          Fixlr Ltd ("Fixlr", "we", "us", "our") provides a smart maintenance management platform for property and facilities teams. This Privacy Policy explains what personal data we collect, how we use it, who we share it with, and the rights you have over it.
        </p>
        <p>
          We act as a <strong>data controller</strong> for personal data we collect about visitors to our marketing website and prospective customers, and as a <strong>data processor</strong> for personal data our customers process inside the Fixlr application. Where we are a processor, our customers are the controllers and you should refer to their privacy notices for those details.
        </p>
      </>
    ),
  },
  {
    id: "data-we-collect",
    title: "What data we collect",
    body: (
      <>
        <p>We collect the following categories of personal data:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Identity & contact data</strong> — name, work email, company name, phone number when you submit a demo request, contact form, or newsletter signup.</li>
          <li><strong>Account data</strong> — login email, hashed password and role for users of the Fixlr application.</li>
          <li><strong>Usage data</strong> — pages visited, features used, performance and error logs, derived using essential cookies and our own analytics.</li>
          <li><strong>Communications</strong> — your messages to support, sales and via the AI chatbot widget on this site.</li>
          <li><strong>Customer-provided data</strong> — when our customers use Fixlr, their data may include details about properties, suppliers, residents, photos and documents. We process this data only on their instructions.</li>
        </ul>
      </>
    ),
  },
  {
    id: "lawful-basis",
    title: "How and why we use it",
    body: (
      <>
        <p>We rely on the following lawful bases under UK GDPR:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Contract</strong> — to provide the Fixlr platform to customers, including authentication, audit trails and support.</li>
          <li><strong>Legitimate interests</strong> — to operate, secure and improve our marketing website, respond to enquiries, run the AI chatbot, and prevent abuse. We balance these interests against your rights.</li>
          <li><strong>Consent</strong> — for marketing newsletter emails. You can withdraw consent at any time using the unsubscribe link.</li>
          <li><strong>Legal obligation</strong> — to keep records for tax, accounting and where law enforcement or a court compels disclosure.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ai-and-third-parties",
    title: "AI and third-party processors",
    body: (
      <>
        <p>
          The chatbot on this website is powered by Anthropic's Claude model via our integration provider. When you send a chat message, the message and the conversation context are sent to Anthropic for the sole purpose of generating a reply. <strong>Your messages are never used to train external AI models.</strong>
        </p>
        <p>We use a small number of carefully chosen processors to run Fixlr:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Cloud hosting (UK / EU regions)</li>
          <li>Transactional email delivery</li>
          <li>Error and performance monitoring</li>
          <li>Anthropic (AI chatbot replies on this site)</li>
        </ul>
        <p>Each processor is bound by a written data processing agreement. We do not sell personal data, and we do not share it for advertising profiling.</p>
      </>
    ),
  },
  {
    id: "retention",
    title: "How long we keep it",
    body: (
      <>
        <p>We keep personal data only as long as we need it for the purposes set out above:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Marketing leads and contact submissions: up to 24 months from last activity.</li>
          <li>Customer account data: for the duration of the contract plus up to 12 months after termination, unless a longer period is required by law.</li>
          <li>AI chatbot conversation history: 30 days, after which it is deleted.</li>
          <li>Backups: rolling 30-day window.</li>
        </ul>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>Under UK GDPR you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Access the personal data we hold about you and request a copy.</li>
          <li>Have inaccurate data corrected, or request erasure.</li>
          <li>Object to or restrict our processing.</li>
          <li>Receive your data in a portable format.</li>
          <li>Withdraw consent at any time (where consent is the lawful basis).</li>
          <li>Lodge a complaint with the UK Information Commissioner's Office (ICO).</li>
        </ul>
        <p>
          To exercise any right, email us at <a href={`mailto:${SITE.email}`} className="text-mk-lime font-semibold">{SITE.email}</a>. We respond within 30 days.
        </p>
      </>
    ),
  },
  {
    id: "international",
    title: "International transfers",
    body: (
      <>
        <p>
          Fixlr is hosted in the UK / EU. Where we use processors outside the UK or EU (for example our AI provider for chatbot replies), the transfer is protected by the UK addendum to the EU Standard Contractual Clauses, plus any additional supplementary measures appropriate to the destination country.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies",
    body: (
      <>
        <p>
          We use a small number of strictly-necessary cookies to keep you signed in and to remember your chatbot session. We do not use third-party advertising cookies. If we add analytics cookies in future, they will be opt-in.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <>
        <p>
          We may update this policy from time to time. The "Last updated" date at the top of the page shows the most recent revision. Material changes will be communicated via email or in-app notice where appropriate.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>
          For privacy questions, requests or complaints, write to us at <a href={`mailto:${SITE.email}`} className="text-mk-lime font-semibold">{SITE.email}</a> or by post to: Fixlr Ltd, c/o Privacy Office, London, United Kingdom.
        </p>
      </>
    ),
  },
];

const Privacy = () => (
  <LegalPageLayout
    slug="privacy"
    eyebrow="Privacy Policy"
    title="Your data, handled the way you'd want."
    lastUpdated="15 January 2026"
    metaTitle="Privacy Policy"
    intro="A plain-English summary of what we collect, how we use it and the controls you have. We process the minimum data needed to run the platform, and we never sell or share it for advertising."
    sections={sections}
  />
);

export default Privacy;
